import { savePreset } from '../../../api';
import getNameForPreset from '../../../helpers/getNameForPreset';
import { controllingSetDefaultPreset } from '../../../store/controlling/actions';
import { defaultPresetSet } from '../../../store/default/actions';
import Back from '../Back';
import Content from '../Content';
import Properties from '../Properties';
import styles from '../common/DevTools.module.scss';
import useDevTools from '../hooks/useDevTools';
import useDefault from './hooks/useDefault';
import useSplitedProperties from './hooks/useSplitedProperties';
import cn from 'classnames';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

const icons = {
  rag: 'rag.svg',
  sobol: 'sobol.svg',
  hog: 'hog.svg',
  chalk: 'chalk.svg',
  custom: 'custom.svg',
};

const types = {
  rag: 'rag',
  sobol: 'sobol',
  hog: 'hog',
  chalk: 'chalk',
};

const PresetDetails = ({ payload }) => {
  const { setMenu } = useDevTools();
  const dispatch = useDispatch();
  const usedBrush = useSelector((state) => state.controlling.defaultPresets[state.controlling.presetType]);
  const properties = useSplitedProperties(payload.id);

  const data = useSelector((state) => {
    const id = payload.id;
    const propertiesObject = state.presets[id];

    const type = types[id.substring(0, id.indexOf('-'))] || 'custom';

    return {
      name: getNameForPreset([id, propertiesObject]),
      id,
      type,
      icon: icons[type],
      editable: propertiesObject.editable,
    };
  });

  const { isDirty, revert } = useDefault(payload.id, data.type);

  useEffect(() => {
    dispatch(controllingSetDefaultPreset(data.type, payload.id));
  }, [data.type, dispatch, payload.id]);

  const propertiesCopy = useSelector((state) => state.presets[payload.id]);

  const save = async () => {
    try {
      await savePreset(payload.id, propertiesCopy);

      dispatch(defaultPresetSet(payload.id, propertiesCopy));

      toast.success('Preset saved!');
    } catch (e) {
      toast.error('Something went wrong');
    }
  };

  const regularAction = (obj) => {
    setMenu(2, 'edit-property', {
      ...obj,
      commitAction: controllingSetDefaultPreset(data.type, payload.id),
      menuIndex: 2,
    });
  };

  const extraAction = (obj) => {
    setMenu(2, 'edit-bezier', {
      ...obj,
      commitAction: controllingSetDefaultPreset(data.type, payload.id),
      menuIndex: 2,
    });
  };

  const name = Array.isArray(data.name) ? data.name[0] : data.name;

  return (
    <>
      <Back title={name} index={payload.menuIndex} />

      <Content>
        <div className={styles.header}>
          <div
            onClick={() => {
              dispatch(controllingSetDefaultPreset(data.type, payload.id));
            }}
            className={cn(styles.icon, usedBrush === payload.id && styles.selected)}
          >
            {data.icon && <img src={`/images/${data.icon}`} alt={data.id} />}
          </div>
          <div className={styles.data}>
            <div className={styles.left}>
              <div className={styles.title}>{name}</div>
              <div className={styles.subTitle}>{data.id}</div>
            </div>
            {isDirty && (
              <div className={styles.right}>
                {data.editable && (
                  <button className={styles.button} onClick={save}>
                    Save
                  </button>
                )}
                <button className={cn(styles.button, styles.revert)} onClick={revert}>
                  Revert all
                </button>
              </div>
            )}
          </div>
        </div>

        <Properties index={payload.menuIndex} data={properties.type} title="Type" regularChange={regularAction} />
        <Properties index={payload.menuIndex} data={properties.shape} title="Shape" regularChange={regularAction} />
        <Properties index={payload.menuIndex} data={properties.physics} title="Physics" regularChange={regularAction} />
        <Properties index={payload.menuIndex} data={properties.simulation} title="Simulation" regularChange={regularAction} />
        <Properties index={payload.menuIndex} data={properties.depletion} title="Depletion" regularChange={regularAction} extraChange={extraAction} />
        <Properties index={payload.menuIndex} data={properties.limitations} title="Limitations" regularChange={regularAction} />
        <Properties index={payload.menuIndex} data={properties.paintRemoving} title="Paint removing" regularChange={regularAction} extraChange={extraAction} />
        <Properties
          index={payload.menuIndex}
          data={properties.primaryBehavior}
          title="Primary behavior"
          regularChange={regularAction}
          extraChange={extraAction}
          importantNote="Behavior on default layer"
        />

        <Properties
          index={payload.menuIndex}
          data={properties.secondaryBehavior}
          title="Secondary behavior"
          regularChange={regularAction}
          extraChange={extraAction}
          importantNote="Behavior on opposite layer"
        />
      </Content>
    </>
  );
};

export default PresetDetails;
